import {
    denormalisedResponseEntities,
} from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { util as sdkUtil } from '../../util/sdkLoader';

// ================ Action types ================ //

export const FETCH_WISHLIST_LISTINGS_REQUEST = 'app/WishlistPage/FETCH_WISHLIST_LISTINGS_REQUEST';
export const FETCH_WISHLIST_LISTINGS_SUCCESS = 'app/WishlistPage/FETCH_WISHLIST_LISTINGS_SUCCESS';
export const FETCH_WISHLIST_LISTINGS_ERROR = 'app/WishlistPage/FETCH_WISHLIST_LISTINGS_ERROR';

export const ADD_TO_WISHLIST_REQUEST = 'app/WishlistPage/ADD_TO_WISHLIST_REQUEST';
export const ADD_TO_WISHLIST_SUCCESS = 'app/WishlistPage/ADD_TO_WISHLIST_SUCCESS';
export const ADD_TO_WISHLIST_ERROR = 'app/WishlistPage/ADD_TO_WISHLIST_ERROR';

export const REMOVE_FROM_WISHLIST_REQUEST = 'app/WishlistPage/REMOVE_FROM_WISHLIST_REQUEST';
export const REMOVE_FROM_WISHLIST_SUCCESS = 'app/WishlistPage/REMOVE_FROM_WISHLIST_SUCCESS';
export const REMOVE_FROM_WISHLIST_ERROR = 'app/WishlistPage/REMOVE_FROM_WISHLIST_ERROR';

export const ADD_WISHLIST_ENTITIES = 'app/WishlistPage/ADD_WISHLIST_ENTITIES';

// ================ Reducer ================ //

const initialState = {
    wishList: [],
    wishlistListings: [],
    queryInProgress: false,
    queryError: null,
    toggleWishlistInProgress: false,
    toggleWishlistError: null,
    togglingId: null,
    hasLoaded: false,
};

const wishlistPageReducer = (state = initialState, action = {}) => {
    const { type, payload } = action;

    switch (type) {
        case FETCH_WISHLIST_LISTINGS_REQUEST:
            return { ...state, queryInProgress: true, queryError: null };
        case FETCH_WISHLIST_LISTINGS_SUCCESS:
            return { ...state, queryInProgress: false, wishlistListings: payload, hasLoaded: true };
        case FETCH_WISHLIST_LISTINGS_ERROR:
            return { ...state, queryInProgress: false, queryError: payload };

        case ADD_TO_WISHLIST_REQUEST:
        case REMOVE_FROM_WISHLIST_REQUEST:
            return {
                ...state,
                toggleWishlistInProgress: true,
                toggleWishlistError: null,
                togglingId: payload.listingId,
            };
        case ADD_TO_WISHLIST_SUCCESS:
        case REMOVE_FROM_WISHLIST_SUCCESS:
            return { ...state, toggleWishlistInProgress: false, wishList: payload, togglingId: null };
        case ADD_TO_WISHLIST_ERROR:
        case REMOVE_FROM_WISHLIST_ERROR:
            return {
                ...state,
                toggleWishlistInProgress: false,
                toggleWishlistError: payload,
                togglingId: null,
            };

        case ADD_WISHLIST_ENTITIES:
            return {
                ...state,
                wishlistListings: payload.data.data,
            };

        default:
            return state;
    }
};

export default wishlistPageReducer;

// ================ Selectors ================ //

export const getWishlistListingIds = state => {
    const { wishList } = state.user?.currentUser?.attributes.profile.publicData || {};
    return wishList || [];
};

// ================ Action creators ================ //

export const fetchWishlistListingsRequest = () => ({ type: FETCH_WISHLIST_LISTINGS_REQUEST });
export const fetchWishlistListingsSuccess = listings => ({
    type: FETCH_WISHLIST_LISTINGS_SUCCESS,
    payload: listings,
});
export const fetchWishlistListingsError = e => ({
    type: FETCH_WISHLIST_LISTINGS_ERROR,
    error: true,
    payload: e,
});

export const addToWishlistRequest = listingId => ({ type: ADD_TO_WISHLIST_REQUEST, payload: { listingId } });
export const addToWishlistSuccess = wishList => ({ type: ADD_TO_WISHLIST_SUCCESS, payload: wishList });
export const addToWishlistError = e => ({ type: ADD_TO_WISHLIST_ERROR, error: true, payload: e });

export const removeFromWishlistRequest = listingId => (
    { type: REMOVE_FROM_WISHLIST_REQUEST, payload: { listingId } }
);
export const removeFromWishlistSuccess = wishList => ({
    type: REMOVE_FROM_WISHLIST_SUCCESS,
    payload: wishList,
});
export const removeFromWishlistError = e => ({
    type: REMOVE_FROM_WISHLIST_ERROR,
    error: true,
    payload: e,
});

export const addWishlistEntities = sdkResponse => ({
    type: ADD_WISHLIST_ENTITIES,
    payload: sdkResponse,
});

// ================ Thunks ================ //

export const queryWishlistListings = (config) => (dispatch, getState, sdk) => {
    dispatch(fetchWishlistListingsRequest());
    const currentUser = getState().user.currentUser;
    const wishList = currentUser?.attributes?.profile?.publicData?.wishList || [];

    if (wishList.length === 0) {
        dispatch(fetchWishlistListingsSuccess([]));
        return Promise.resolve([]);
    }

    const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
    } = config?.layout?.listingImage || {};
    const aspectRatio = aspectHeight / aspectWidth;

    const params = {
        ids: wishList,
        include: ['author', 'images', 'review'],
        'fields.listing': [
            'title',
            'geolocation',
            'price',
            'deleted',
            'state',
            'publicData.listingType',
            'publicData.transactionProcessAlias',
            'publicData.unitType',
            'publicData.location',
            'publicData.isFeatured',
            'publicData.totalRatingSum',
            'publicData.totalRatings',
            'publicData.price_per_day',
        ],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
        'fields.image': [
            'variants.scaled-small',
            'variants.scaled-medium',
            `variants.${variantPrefix}`,
            `variants.${variantPrefix}-2x`,
        ],
        'imageVariant.listing-card': sdkUtil.objectQueryString({
            w: 400,
            fit: 'scale',
        }),
        'imageVariant.listing-card-2x': sdkUtil.objectQueryString({
            w: 800,
            fit: 'scale',
        }),
    };

    return sdk.listings
        .query(params)
        .then(response => {
            dispatch(addWishlistEntities(response));
            const denormalisedListings = denormalisedResponseEntities(response);
            dispatch(fetchWishlistListingsSuccess(denormalisedListings));
            return response;
        })
        .catch(e => {
            dispatch(fetchWishlistListingsError(storableError(e)));
            throw e;
        });
};

export const addToWishlist = listingId => (dispatch, getState, sdk) => {
    dispatch(addToWishlistRequest(listingId));
    const currentUser = getState().user.currentUser;
    const wishList = currentUser?.attributes?.profile?.publicData?.wishList || [];
    const newWishlist = [...wishList, listingId];

    return sdk.currentUser
        .updateProfile(
            {
                publicData: {
                    wishList: newWishlist,
                },
            },
            { expand: true }
        )
        .then(response => {
            const entities = denormalisedResponseEntities(response);
            const currentUser = entities[0];
            dispatch(addToWishlistSuccess(currentUser.attributes.profile.publicData.wishList));
            dispatch(fetchCurrentUser())
            return response;
        })
        .catch(e => {
            dispatch(addToWishlistError(storableError(e)));
        });
};


// remove from wishlist
export const removeFromWishlist = listingId => (dispatch, getState, sdk) => {
    dispatch(removeFromWishlistRequest(listingId));
    const currentUser = getState().user.currentUser;
    const wishList = currentUser?.attributes?.profile?.publicData?.wishList || [];
    const newWishlist = wishList.filter(id => id !== listingId);

    return sdk.currentUser
        .updateProfile(
            {
                publicData: {
                    wishList: newWishlist,
                },
            },
            { expand: true }
        )
        .then(response => {
            const entities = denormalisedResponseEntities(response);
            const currentUser = entities[0];
            dispatch(removeFromWishlistSuccess(currentUser.attributes.profile.publicData.wishList));
            dispatch(fetchCurrentUser()).then(() => {
                dispatch(queryWishlistListings());
            });
            return response;
        })
        .catch(e => {
            dispatch(removeFromWishlistError(storableError(e)));
        });
};

export const loadData = (config) => (dispatch, getState, sdk) => {
    return dispatch(fetchCurrentUser()).then(() => {
        return dispatch(queryWishlistListings(config));
    });
};


export const wishListListingsSelector = state => state.WishListPage.wishlistListings;